import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import EventForm from '@/components/admin/EventForm';
import { AIToolsCard } from '@/components/admin/AITools';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Copy, Eye, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

export default function AdminEventEditPage() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateData, setDuplicateData] = useState({
    slug: '',
    startDate: '',
    endDate: '',
    displayDate: ''
  });
  const [duplicating, setDuplicating] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/events/${id}`, {
        headers: getAuthHeaders()
      });
      setEvent(response.data);
    } catch (error) {
      console.error('Failed to fetch event:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else if (error.response?.status === 404) {
        toast.error('Event not found');
        navigate('/admin/events');
      } else {
        toast.error('Failed to load event');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (formData) => {
    try {
      setSaving(true);
      const response = await axios.put(`${API}/admin/events/${id}`, formData, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        toast.success('Event updated successfully');
        setEvent(response.data.event);
      }
    } catch (error) {
      console.error('Failed to update event:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Invalid event data');
      } else {
        toast.error('Failed to update event');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      setDuplicating(true);
      const response = await axios.post(
        `${API}/admin/events/${id}/duplicate`,
        {
          slug: duplicateData.slug,
          dates: {
            start: duplicateData.startDate,
            end: duplicateData.endDate,
            display: duplicateData.displayDate
          }
        },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        toast.success('Event duplicated successfully');
        setDuplicateDialogOpen(false);
        // Navigate to the new event
        navigate(`/admin/events/${response.data.event.id}`);
      }
    } catch (error) {
      console.error('Failed to duplicate event:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Event with this slug already exists');
      } else {
        toast.error('Failed to duplicate event');
      }
    } finally {
      setDuplicating(false);
    }
  };

  const viewPublicPage = () => {
    if (event) {
      const url = `/events/${event.citySlug}/${event.venueSlug}/${event.slug}`;
      window.open(url, '_blank');
    }
  };

  const handleSendMagicLink = async () => {
    try {
      await axios.post(
        `${API}/admin/events/${id}/send-magic-link`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Magic link sent', {
        description: `Manage link sent to ${event.ownerEmail}`
      });
    } catch (error) {
      console.error('Failed to send magic link:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Cannot send magic link');
      } else {
        toast.error('Failed to send magic link');
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading event...</p>
        </div>
      </AdminLayout>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/events')}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Event</h1>
              <p className="text-gray-600 mt-1">{event.name}</p>
            </div>
          </div>

          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={viewPublicPage}
              className="flex items-center"
              data-testid="view-public-button"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Public Page
            </Button>

            {event.ownerType === 'organiser' && event.ownerEmail && (
              <Button
                variant="outline"
                onClick={handleSendMagicLink}
                className="flex items-center"
                data-testid="send-magic-link-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Magic Link
              </Button>
            )}

            <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex items-center" data-testid="duplicate-button">
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate as New Edition
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Duplicate Event as New Edition</DialogTitle>
                  <DialogDescription>
                    Create a new event based on this one with updated dates and slug.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="duplicate-slug">New Slug *</Label>
                    <Input
                      id="duplicate-slug"
                      value={duplicateData.slug}
                      onChange={(e) => setDuplicateData(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="event-name-2027"
                      data-testid="duplicate-slug-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="duplicate-start">Start Date *</Label>
                    <Input
                      type="date"
                      id="duplicate-start"
                      value={duplicateData.startDate}
                      onChange={(e) => setDuplicateData(prev => ({ ...prev, startDate: e.target.value }))}
                      data-testid="duplicate-start-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="duplicate-end">End Date *</Label>
                    <Input
                      type="date"
                      id="duplicate-end"
                      value={duplicateData.endDate}
                      onChange={(e) => setDuplicateData(prev => ({ ...prev, endDate: e.target.value }))}
                      data-testid="duplicate-end-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="duplicate-display">Display String *</Label>
                    <Input
                      id="duplicate-display"
                      value={duplicateData.displayDate}
                      onChange={(e) => setDuplicateData(prev => ({ ...prev, displayDate: e.target.value }))}
                      placeholder="11–12 November 2027"
                      data-testid="duplicate-display-date"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
                  <Button
                    onClick={handleDuplicate}
                    disabled={duplicating || !duplicateData.slug || !duplicateData.startDate || !duplicateData.endDate || !duplicateData.displayDate}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="confirm-duplicate-button"
                  >
                    {duplicating ? 'Creating...' : 'Create New Edition'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* AI Tools Card */}
        <AIToolsCard eventId={event.id} eventName={event.name} />

        {/* Form */}
        <EventForm
          initialData={event}
          onSubmit={handleUpdate}
          loading={saving}
          submitLabel="Save Changes"
        />
      </div>
    </AdminLayout>
  );
}